import React, { useMemo, useState } from 'react';
import {
  Card,
  DatePicker,
  Select,
  Table,
  Row,
  Col,
  Statistic,
  Switch,
  Space,
  Button,
} from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;

const applyBreakDeduction = (hoursWorked) => {
  if (hoursWorked <= 5) return hoursWorked;

  const breaks =
    Math.floor(hoursWorked / 5) * 0.5;

  return hoursWorked - breaks;
};

const calculateHours = (
  date,
  startTime,
  endTime
) => {
  let start = dayjs(
    `${date} ${startTime}`,
    'DD-MM-YYYY HH:mm'
  );

  let end = dayjs(
    `${date} ${endTime}`,
    'DD-MM-YYYY HH:mm'
  );

  if (end.isBefore(start)) {
    end = end.add(1, 'day');
  }

  return end.diff(start, 'minute') / 60;
};

const TimesheetHoursReport = ({
  timesheets,
}) => {
  const [approvedOnly, setApprovedOnly] =
    useState(true);

  const [
    includeMealBreak,
    setIncludeMealBreak,
  ] = useState(true);

  const [
    selectedEmployee,
    setSelectedEmployee,
  ] = useState();

  const [dateRange, setDateRange] =
    useState([
      dayjs().startOf('month'),
      dayjs().endOf('month'),
    ]);

  const employees = useMemo(() => {
    const map = new Map();

    timesheets.forEach((x) => {
      map.set(x.staffId, {
        label: x.staffName,
        value: x.staffId,
      });
    });

    return [...map.values()];
  }, [timesheets]);

  const reportRows = useMemo(() => {
    if (!dateRange) return [];

    let filtered = [...timesheets];

    filtered = filtered.filter((item) => {
      const d = dayjs(
        item.date,
        'DD-MM-YYYY'
      );

      return (
        d.isAfter(
          dateRange[0].subtract(
            1,
            'day'
          )
        ) &&
        d.isBefore(
          dateRange[1].add(
            1,
            'day'
          )
        )
      );
    });

    if (approvedOnly) {
      filtered = filtered.filter(
        (x) =>
          x.approvalStatus ===
          'approved'
      );
    }

    if (selectedEmployee) {
      filtered = filtered.filter(
        (x) =>
          x.staffId ===
          selectedEmployee
      );
    }

    const grouped = {};

    filtered.forEach((ts) => {
      let hours =
        calculateHours(
          ts.date,
          ts.starttime,
          ts.endtime
        );

      if (includeMealBreak) {
        hours =
          applyBreakDeduction(hours);
      }

      if (!grouped[ts.staffId]) {
        grouped[ts.staffId] = {
          key: ts.staffId,
          staffName: ts.staffName,
          totalHours: 0,
        };
      }

      grouped[ts.staffId][ts.date] =
        (grouped[ts.staffId][
          ts.date
        ] || 0) + hours;

      grouped[
        ts.staffId
      ].totalHours += hours;
    });

    return Object.values(grouped);
  }, [
    timesheets,
    dateRange,
    selectedEmployee,
    approvedOnly,
    includeMealBreak,
  ]);

  const downloadTimesheets = () => {
    if (!dateRange) return;

    const dates = [];
    let current = dateRange[0];

    while (
      current.isBefore(
        dateRange[1].add(
          1,
          'day'
        )
      )
    ) {
      dates.push(
        current.format(
          'DD-MM-YYYY'
        )
      );

      current =
        current.add(1, 'day');
    }

    const headers = [
      'Employee',
      ...dates,
      'Total Hours',
    ];

    const rows = reportRows.map(
      (row) => {
        const daily = dates.map(
          (d) =>
            row[d]
              ? Number(
                  row[d]
                ).toFixed(2)
              : '0.00'
        );

        return [
          row.staffName,
          ...daily,
          Number(
            row.totalHours || 0
          ).toFixed(2),
        ];
      }
    );

    const csv = [
      headers.join(','),
      ...rows.map((r) =>
        r.join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;',
    });

    const url =
      URL.createObjectURL(blob);

    const a =
      document.createElement('a');

    a.href = url;

    a.download = `timesheets_${dateRange[0].format(
      'YYYYMMDD'
    )}_${dateRange[1].format(
      'YYYYMMDD'
    )}.csv`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const dailyColumns = useMemo(() => {
    if (!dateRange) return [];

    const cols = [];

    let current = dateRange[0];

    while (
      current.isBefore(
        dateRange[1].add(
          1,
          'day'
        )
      )
    ) {
      const dateKey =
        current.format(
          'DD-MM-YYYY'
        );

      cols.push({
        title:
          current.format(
            'DD MMM'
          ),
        dataIndex: dateKey,
        width: 100,
        render: (value) =>
          value
            ? value.toFixed(2)
            : '',
      });

      current =
        current.add(1, 'day');
    }

    return cols;
  }, [dateRange]);

  const totalHours =
    reportRows.reduce(
      (sum, row) =>
        sum + row.totalHours,
      0
    );

  const summaryColumns = [
    {
      title: 'Employee',
      dataIndex: 'staffName',
    },
    {
      title: 'Total Hours',
      dataIndex: 'totalHours',
      render: (value) =>
        value.toFixed(2),
    },
  ];

  return (
    <Space
      direction="vertical"
      size={16}
      style={{ width: '100%' }}
    >
      <Card>
        <Row gutter={16}>
          <Col>
            <RangePicker
              value={dateRange}
              onChange={
                setDateRange
              }
            />
          </Col>

          <Col>
            <Select
              allowClear
              style={{
                width: 250,
              }}
              placeholder="Employee"
              value={
                selectedEmployee
              }
              onChange={
                setSelectedEmployee
              }
              options={
                employees
              }
            />
          </Col>

          <Col>
            <Space>
              Approved Only
              <Switch
                checked={
                  approvedOnly
                }
                onChange={
                  setApprovedOnly
                }
              />
            </Space>
          </Col>

          <Col>
            <Space>
              apply Meal Break
              <Switch
                checked={
                  includeMealBreak
                }
                onChange={
                  setIncludeMealBreak
                }
              />
            </Space>
          </Col>

          <Col>
            <Button
              type="primary"
              onClick={
                downloadTimesheets
              }
            >
              Download Timesheets
            </Button>
          </Col>
        </Row>
      </Card>

      <Row gutter={16}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Hours"
              value={totalHours.toFixed(
                2
              )}
            />
          </Card>
        </Col>

        <Col span={8}>
          <Card>
            <Statistic
              title="Employees"
              value={
                reportRows.length
              }
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={
          includeMealBreak
            ? 'Daily Breakdown (meal breaks deducted)'
            : 'Daily Breakdown (raw hours)'
        }
      >
        <Table
          bordered
          size="small"
          rowKey="key"
          pagination={false}
          scroll={{
            x: 'max-content',
          }}
          columns={[
            {
              title:
                'Employee',
              dataIndex:
                'staffName',
              fixed: 'left',
              width: 200,
            },

            ...dailyColumns,

            {
              title:
                'Total Hours',
              dataIndex:
                'totalHours',
              fixed: 'right',
              width: 150,
              render: (
                value
              ) =>
                value.toFixed(
                  2
                ),
            },
          ]}
          dataSource={
            reportRows
          }
        />
      </Card>

      <Card title="Employee Summary">
        <Table
          rowKey="key"
          pagination={false}
          columns={
            summaryColumns
          }
          dataSource={
            reportRows
          }
        />
      </Card>
    </Space>
  );
};

export default TimesheetHoursReport;